import React, { useEffect, useRef, useState } from "react";
import useAuth from "src/hooks/useAuth";
import {
  Box,
  Chip,
  Popover,
  Typography,
  Stack,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import xIcon from "@iconify-icons/eva/close-fill";
import { useDispatch } from "src/redux/store";
import { dismissOnboardingForToday } from "src/redux/slices/onboarding";
import useOnboardingProgress from "src/hooks/useOnboardingProgress";
import OnboardingWizardModal from "src/views/onboarding/OnboardingWizardModal";

// ----------------------------------------------------------------------
//
//   Chip compacto en TopBar con % de progreso del wizard. Popover al
//   click con resumen + CTA "Continuar". El chip se oculta si el user
//   completó el onboarding o pidió posponerlo hoy.
//
//   Convención de steps visuales:
//     welcome · zonas · mesas · carta · impresoras · worker · asistencia
//
// ----------------------------------------------------------------------

// 2026-07-28 · wizard reducido a 7 steps. Los codes legacy siguen
// mapeados para que users antiguos con `completedSteps` viejo no vean
// el code crudo en el popover del TopBar.
const STEP_LABELS = {
  empresa: "Datos empresa",
  subscription: "Suscripción",
  local: "Local",
  carta: "Carta",
  hardware: "Hardware",
  checkout: "Pago",
  finish: "¡Bienvenido!",
  // Legacy 13-step v1:
  welcome: "Bienvenida",
  plan: "Plan",
  addons: "Extras",
  support: "Soporte",
  zonas: "Zonas",
  mesas: "Mesas",
  impresoras: "Impresoras",
  worker: "Worker",
  asistencia: "Instalación asistida",
};

// Marcador de "ya te abrí el modal en esta pestaña" — evita reabrirlo
// cada vez que el user cambia de ruta. Guardamos el userId como valor
// para que un nuevo login (mismo o distinto user) NO herede el
// marcador de la sesión anterior — si el user hizo logout y volvió
// a entrar, esperamos que el modal se auto-abra de nuevo.
const AUTO_OPENED_KEY = "tmo_onboarding_auto_opened";

export default function OnboardingWidget() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openWizardModal, setOpenWizardModal] = useState(false);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { progress, isVisible, shouldAutoOpen, isMandatory } = useOnboardingProgress();
  const prevUserIdRef = useRef(null);

  // Al cambiar de user (login/logout+login), limpiamos el marcador
  // para que el auto-open vuelva a dispararse. Sin esto, cerrar el
  // modal + logout + login dejaba el chip sin abrirlo — solo se
  // podía disparar manualmente desde el chip.
  useEffect(() => {
    const currentId = user?.id || null;
    if (prevUserIdRef.current !== currentId) {
      prevUserIdRef.current = currentId;
      try {
        sessionStorage.removeItem(AUTO_OPENED_KEY);
      } catch {
        /* noop */
      }
    }
  }, [user?.id]);

  // Auto-abrir el modal al aterrizar por primera vez en el portal si
  // el onboarding no está completo y el user NO pidió posponerlo hoy.
  // El chip del TopBar sigue apareciendo siempre (`isVisible`),
  // permitiendo abrir el wizard manualmente en cualquier momento.
  useEffect(() => {
    if (!shouldAutoOpen || !progress) return;
    if (openWizardModal) return;
    try {
      if (sessionStorage.getItem(AUTO_OPENED_KEY)) return;
      sessionStorage.setItem(AUTO_OPENED_KEY, "1");
    } catch {
      /* sessionStorage bloqueado → abrimos igualmente */
    }
    setOpenWizardModal(true);
  }, [shouldAutoOpen, progress, openWizardModal]);

  if (!isVisible) return null;

  const handleContinue = () => {
    // Cambio 2026-07-22: abrimos MODAL en vez de navegar a `/wizard`.
    // El user pidió no salir de la vista principal. La ruta `/wizard`
    // sigue disponible para el aterrizaje post-registro.
    setAnchorEl(null);
    setOpenWizardModal(true);
  };
  const handleDismiss = async (e) => {
    e.stopPropagation();
    await dispatch(dismissOnboardingForToday());
  };

  const remainingSteps = Object.keys(STEP_LABELS).filter(
    (s) => !(progress.completedSteps || []).includes(s),
  );

  return (
    <>
      <Chip
        clickable
        onClick={(e) => setAnchorEl(e.currentTarget)}
        label={
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" sx={{ fontWeight: 800 }}>
              🎯 Onboarding {progress.progressPercent}%
            </Typography>
            <Box
              sx={{
                width: 40, height: 4, borderRadius: 2,
                bgcolor: "rgba(0,0,0,0.1)",
                overflow: "hidden",
              }}
            >
              <Box sx={{
                width: `${progress.progressPercent}%`,
                height: "100%",
                bgcolor: "success.main",
                transition: "width .3s",
              }} />
            </Box>
          </Stack>
        }
        sx={{
          bgcolor: "warning.lighter",
          color: "warning.dark",
          fontWeight: 700,
          height: 28,
          "&:hover": { bgcolor: "warning.light" },
        }}
      />

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { p: 2.5, width: 340, mt: 1 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Termina tu configuración
          </Typography>
          <Tooltip title="Recordar mañana">
            <IconButton size="small" onClick={handleDismiss}>
              <Icon icon={xIcon} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {progress.progressPercent}% completado · {remainingSteps.length} pasos por delante
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress.progressPercent}
          color="success"
          sx={{ mt: 1, mb: 2, height: 6, borderRadius: 3 }}
        />

        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {Object.entries(STEP_LABELS).map(([code, label]) => {
            const done = (progress.completedSteps || []).includes(code);
            return (
              <Stack direction="row" spacing={1} key={code} alignItems="center">
                <Box sx={{
                  width: 18, height: 18, borderRadius: "50%",
                  bgcolor: done ? "success.main" : "grey.200",
                  color: done ? "white" : "grey.500",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 800,
                }}>
                  {done ? "✓" : "·"}
                </Box>
                <Typography variant="body2" sx={{ color: done ? "text.disabled" : "text.primary", textDecoration: done ? "line-through" : "none" }}>
                  {label}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        <Button
          fullWidth
          variant="contained"
          color="success"
          onClick={handleContinue}
        >
          Continuar configuración
        </Button>
      </Popover>

      <OnboardingWizardModal
        open={openWizardModal}
        onClose={() => setOpenWizardModal(false)}
        mandatory={isMandatory}
      />
    </>
  );
}
